import React, { useState, useEffect, useRef } from "react";
import {
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  CameraIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  CalendarIcon,
  IdentificationIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import {
  CreateEmployeeService,
  GetOptionsService,
} from "../services/ApiService";

// ✅ Searchable Dropdown with "Create New" Option
const SearchableDropdown = ({
  label,
  icon: Icon,
  options,
  value,
  onChange,
  placeholder,
  disabled,
  loading,
  error,
  name,
  onCreateNew,
  dropUp = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Check if user is searching for something that doesn't exist
  const showCreateOption =
    searchTerm &&
    !filteredOptions.some(
      (opt) => opt.toLowerCase() === searchTerm.toLowerCase(),
    );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange({ target: { name, value: option } });
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleCreateNew = () => {
    if (onCreateNew && searchTerm.trim()) {
      onCreateNew(searchTerm.trim());
      setSearchTerm("");
      setIsOpen(false);
    }
  };

  return (
    <div className="space-y-3" ref={dropdownRef}>
      <label className="block">
        <span className="text-sm font-medium text-gray-700 mb-2 flex items-center">
          {Icon && <Icon className="w-4 h-4 mr-2 text-emerald-500" />}
          {label}
        </span>

        {/* Selected Value Display */}
        <div className="relative">
          <button
            type="button"
            onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
            disabled={disabled || loading}
            className={`w-full px-4 py-3 rounded-xl border ${
              error ? "border-red-300 bg-red-50" : "border-emerald-200"
            } bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
              hover:border-emerald-300 transition-colors duration-200 disabled:bg-gray-100 
              disabled:cursor-not-allowed text-left flex items-center justify-between`}
          >
            <span className={value ? "text-gray-900" : "text-gray-400"}>
              {loading
                ? `Loading ${label.toLowerCase()}...`
                : value || placeholder}
            </span>
            <ChevronDownIcon
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                isOpen ? "transform rotate-180" : ""
              }`}
            />
          </button>

          {/* Dropdown Menu */}
          {isOpen && !disabled && !loading && (
            <div
              className={`absolute z-50 w-full bg-white border border-emerald-200 rounded-xl shadow-2xl max-h-80 overflow-hidden 
              ${dropUp ? "bottom-full mb-2" : "top-full mt-2"} 
              `}
            >
              {" "}
              {/* Search Input */}
              <div className="p-3 border-b border-gray-200 sticky top-0 bg-white">
                <div className="relative">
                  <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={`Search ${label.toLowerCase()}...`}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm"
                    autoFocus
                  />
                </div>
              </div>
              {/* Options List */}
              <div className="max-h-60 overflow-y-auto">
                {/* ✅ CREATE NEW OPTION */}
                {showCreateOption && onCreateNew && (
                  <button
                    type="button"
                    onClick={handleCreateNew}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 transition-colors text-sm border-b border-gray-100 flex items-center gap-2 text-emerald-600 font-medium"
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span>Create "{searchTerm}"</span>
                  </button>
                )}

                {/* EXISTING OPTIONS */}
                {filteredOptions.length > 0
                  ? filteredOptions.map((option, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className={`w-full px-4 py-3 text-left hover:bg-emerald-50 transition-colors text-sm ${
                          value === option
                            ? "bg-emerald-100 text-emerald-700 font-medium"
                            : "text-gray-700"
                        }`}
                      >
                        {option}
                      </button>
                    ))
                  : !showCreateOption && (
                      <div className="px-4 py-8 text-center text-gray-500">
                        <p className="text-sm font-medium">No results found</p>
                        <p className="text-xs mt-1 text-gray-400">
                          Try searching with different keywords
                        </p>
                      </div>
                    )}
              </div>
            </div>
          )}
        </div>

        {error && (
          <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
            <ExclamationCircleIcon className="w-4 h-4" />
            {error}
          </p>
        )}
      </label>
    </div>
  );
};

const CreateEmployeeForm = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    employee_code: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    phone: "",
    phone_code: "+91",
    department: "",
    position: "",
    date_of_joining: "",
    file: null,
    photoPreview: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ Dynamic dropdowns state
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [loadingPositions, setLoadingPositions] = useState(false);

  const countryCodes = [
    { code: "+91", country: "India", flag: "🇮🇳" },
    { code: "+1", country: "USA", flag: "🇺🇸" },
    { code: "+44", country: "UK", flag: "🇬🇧" },
    { code: "+61", country: "Australia", flag: "🇦🇺" },
    { code: "+65", country: "Singapore", flag: "🇸🇬" },
    { code: "+971", country: "UAE", flag: "🇦🇪" },
    { code: "+33", country: "France", flag: "🇫🇷" },
    { code: "+49", country: "Germany", flag: "🇩🇪" },
    { code: "+81", country: "Japan", flag: "🇯🇵" },
    { code: "+82", country: "South Korea", flag: "🇰🇷" },
  ];

  // ✅ Fetch departments on mount
  useEffect(() => {
    fetchDepartments();
  }, []);

  // ✅ Fetch positions when department changes
  useEffect(() => {
    if (formData.department) {
      fetchPositions();
    } else {
      setPositions([]);
    }
  }, [formData.department]);

  const fetchDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const response = await GetOptionsService("DEPARTMENT");
      setDepartments(response || []);
    } catch (error) {
      console.error("Failed to fetch departments:", error);
      setErrors((prev) => ({
        ...prev,
        submit: "Failed to load departments. Please refresh the page.",
      }));
    } finally {
      setLoadingDepartments(false);
    }
  };

  const fetchPositions = async () => {
    try {
      setLoadingPositions(true);
      const response = await GetOptionsService("POSITION");
      setPositions(response || []);
    } catch (error) {
      console.error("Failed to fetch positions:", error);
      setErrors((prev) => ({
        ...prev,
        position: "Failed to load positions.",
      }));
      setPositions([]);
    } finally {
      setLoadingPositions(false);
    }
  };

  // ✅ NEW: Handle creating new department
  const handleCreateDepartment = (newDept) => {
    // Add to local state immediately
    setDepartments((prev) => [...prev, newDept]);
    // Set as selected value
    setFormData((prev) => ({ ...prev, department: newDept, position: "" }));
    console.log("✅ New department created:", newDept);
  };

  // ✅ NEW: Handle creating new position
  const handleCreatePosition = (newPos) => {
    // Add to local state immediately
    setPositions((prev) => [...prev, newPos]);
    // Set as selected value
    setFormData((prev) => ({ ...prev, position: newPos }));
    console.log("✅ New position created:", newPos);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (name === "department") {
      setFormData((prev) => ({ ...prev, position: "" }));
      if (errors.position) {
        setErrors((prev) => ({ ...prev, position: "" }));
      }
    }
  };

  const handlePhoneInput = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 0) {
      value = value.match(/.{1,5}/g).join(" ");
      if (value.length > 14) value = value.slice(0, 14);
    }
    setFormData((prev) => ({ ...prev, phone: value }));
    if (errors.phone) setErrors((prev) => ({ ...prev, phone: "" }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({
        ...prev,
        file: "Please select an image file (JPG, PNG, GIF)",
      }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        file: "Image size should be less than 5MB",
      }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        file,
        photoPreview: reader.result,
      }));
      if (errors.file) setErrors((prev) => ({ ...prev, file: "" }));
    };
    reader.readAsDataURL(file);
  };

  const removeFile = () => {
    setFormData((prev) => ({ ...prev, file: null, photoPreview: "" }));
    if (errors.file) setErrors((prev) => ({ ...prev, file: "" }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.employee_code.trim())
      newErrors.employee_code = "Employee code is required";
    else if (formData.employee_code.length < 3)
      newErrors.employee_code = "Employee code must be at least 3 characters";

    if (!formData.first_name.trim())
      newErrors.first_name = "First name is required";
    else if (formData.first_name.length < 2)
      newErrors.first_name = "First name must be at least 2 characters";

    if (!formData.last_name.trim())
      newErrors.last_name = "Last name is required";
    else if (formData.last_name.length < 2)
      newErrors.last_name = "Last name must be at least 2 characters";

    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Please enter a valid email address";

    const cleanPhone = formData.phone.replace(/\s/g, "");
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else if (cleanPhone.length < 10)
      newErrors.phone = "Phone number must be at least 10 digits";

    if (!formData.department)
      newErrors.department = "Please select a department";

    if (!formData.position) newErrors.position = "Please select a position";

    if (!formData.date_of_joining)
      newErrors.date_of_joining = "Please select a hire date";
    else if (new Date(formData.date_of_joining) > new Date())
      newErrors.date_of_joining = "Hire date cannot be in the future";

    if (!formData.file) newErrors.file = "Profile photo is required";

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors((prev) => ({ ...prev, submit: "" }));

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const dataToSend = new FormData();

      dataToSend.append("employee_id", formData.employee_code);
      dataToSend.append("first_name", formData.first_name);
      dataToSend.append("middle_name", formData.middle_name || "");
      dataToSend.append("last_name", formData.last_name);
      dataToSend.append("email", formData.email);

      const normalizedPhone = `${formData.phone_code}${formData.phone.replace(/\s/g, "")}`;
      dataToSend.append("phone", normalizedPhone);

      dataToSend.append("department", formData.department);
      dataToSend.append("position", formData.position);

      const joiningDate = new Date(formData.date_of_joining)
        .toISOString()
        .split("T")[0];
      dataToSend.append("date_of_joining", joiningDate);

      if (formData.file) dataToSend.append("file", formData.file);

      const response = await CreateEmployeeService(dataToSend);

      onSubmit({
        employee: {
          ...response,
          phone: normalizedPhone,
        },
        credentials: {
          employee_id: response?.employee_id,
          temporary_password: response?.password,
          message: response?.message,
        },
      });

      onClose();
    } catch (err) {
      console.error("Create employee error:", err);

      let errorMessage = "Failed to create employee. Please try again.";
      let fieldErrors = {};

      const backendError = err?.response?.data || err?.data || null;

      if (backendError) {
        if (backendError.field && backendError.message) {
          fieldErrors[backendError.field] = backendError.message;
          errorMessage = backendError.message;
        } else if (backendError.message) {
          errorMessage = backendError.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setErrors((prev) => ({
        ...prev,
        ...fieldErrors,
        submit: errorMessage,
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-300 border border-emerald-100"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-8 py-6 border-b border-emerald-100 bg-gradient-to-r from-emerald-50 to-green-50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Create New Employee
                </h2>
                <p className="text-emerald-600 mt-1">
                  Fill in all required details
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-green-50 rounded-xl transition-all duration-200 border border-transparent hover:border-emerald-200 group"
                aria-label="Close"
              >
                <XMarkIcon className="w-6 h-6 text-gray-400 group-hover:text-emerald-600" />
              </button>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-8">
            {/* Global Error Alert */}
            {errors.submit && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-start gap-3">
                <ExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">
                    {errors.submit}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setErrors((prev) => ({ ...prev, submit: "" }))}
                  className="text-red-400 hover:text-red-600 transition-colors"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Profile Photo */}
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-200 hover:border-emerald-300 transition-colors duration-200">
                  <h3 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider mb-4 flex items-center">
                    <CameraIcon className="w-4 h-4 mr-2" /> Profile Photo
                  </h3>
                  <div className="flex flex-col items-center">
                    {formData.photoPreview ? (
                      <div className="relative mb-6 group">
                        <div className="w-40 h-35 rounded-2xl overflow-hidden ring-4 ring-white shadow-xl border-2 border-emerald-200">
                          <img
                            src={formData.photoPreview}
                            alt="Preview"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="absolute -top-2 -right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gradient-to-r hover:from-emerald-50 hover:to-green-50 transition-all duration-200 border border-emerald-200 group/remove"
                        >
                          <XMarkIcon className="w-4 h-4 text-gray-600 group-hover/remove:text-emerald-600" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-40 h-40 mb-6 flex items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-50 to-green-100 border-2 border-dashed border-emerald-300 hover:border-emerald-400 transition-colors duration-200">
                        <UserCircleIcon className="w-20 h-20 text-emerald-400" />
                      </div>
                    )}
                    <div className="w-full">
                      <label className="cursor-pointer group/upload">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        <div className="flex items-center justify-center px-4 py-3 text-sm font-medium text-gray-700 bg-white hover:bg-gradient-to-r hover:from-emerald-50 hover:to-green-50 rounded-xl border border-gray-200 hover:border-emerald-300 transition-all duration-200 w-full group-hover/upload:shadow-sm">
                          <ArrowUpTrayIcon className="w-5 h-5 mr-2 text-gray-500 group-hover/upload:text-emerald-600" />
                          {formData.photoPreview
                            ? "Change Photo"
                            : "Upload Photo"}
                        </div>
                      </label>
                      <p className="text-xs text-gray-500 text-center mt-3">
                        JPG, PNG or GIF • Max 5MB
                      </p>
                    </div>
                  </div>
                  {errors.file && (
                    <p className="text-red-500 text-sm mt-3 text-center">
                      {errors.file}
                    </p>
                  )}
                </div>

                {/* Employee Code */}
                <div className="space-y-3">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <IdentificationIcon className="w-4 h-4 mr-2 text-emerald-500" />
                      Employee Code *
                    </span>
                    <input
                      type="text"
                      name="employee_code"
                      value={formData.employee_code}
                      onChange={handleChange}
                      placeholder="CFO_0001"
                      className={`w-full px-4 py-3 rounded-xl border ${
                        errors.employee_code
                          ? "border-red-300 bg-red-50"
                          : "border-emerald-200"
                      } focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
                    />
                    {errors.employee_code && (
                      <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                        <ExclamationCircleIcon className="w-4 h-4" />
                        {errors.employee_code}
                      </p>
                    )}
                  </label>
                </div>

                {/* ✅ Searchable Department Dropdown with CREATE */}
                <SearchableDropdown
                  label="Department *"
                  icon={BuildingOfficeIcon}
                  name="department"
                  options={departments}
                  value={formData.department}
                  onChange={handleChange}
                  onCreateNew={handleCreateDepartment}
                  placeholder="Select or create department"
                  disabled={false}
                  loading={loadingDepartments}
                  error={errors.department}
                />

                {/* ✅ Searchable Position Dropdown with CREATE */}
                <SearchableDropdown
                  label="Position *"
                  name="position"
                  options={positions}
                  value={formData.position}
                  onChange={handleChange}
                  onCreateNew={handleCreatePosition}
                  placeholder={
                    formData.department
                      ? "Select or create position"
                      : "Select department first"
                  }
                  disabled={!formData.department}
                  loading={loadingPositions}
                  error={errors.position}
                  dropUp={true}
                />
              </div>

              {/* Right Column - Keep all existing fields */}
              <div className="space-y-6">
                {/* First Name, Middle Name, Last Name */}
                <div className="space-y-4">
                  <div className="space-y-3">
                    <label className="block">
                      <span className="text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </span>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        placeholder="John"
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.first_name
                            ? "border-red-300"
                            : "border-emerald-200"
                        } focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
                      />
                      {errors.first_name && (
                        <p className="text-red-500 text-sm mt-2">
                          {errors.first_name}
                        </p>
                      )}
                    </label>
                  </div>

                  <div className="space-y-3">
                    <label className="block">
                      <span className="text-sm font-medium text-gray-700 mb-2">
                        Middle Name (Optional)
                      </span>
                      <input
                        type="text"
                        name="middle_name"
                        value={formData.middle_name}
                        onChange={handleChange}
                        placeholder="Michael"
                        className="w-full px-4 py-3 rounded-xl border border-emerald-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </label>
                  </div>

                  <div className="space-y-3">
                    <label className="block">
                      <span className="text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </span>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        placeholder="Doe"
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.last_name
                            ? "border-red-300"
                            : "border-emerald-200"
                        } focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
                      />
                      {errors.last_name && (
                        <p className="text-red-500 text-sm mt-2">
                          {errors.last_name}
                        </p>
                      )}
                    </label>
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-3">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <EnvelopeIcon className="w-4 h-4 mr-2 text-emerald-500" />
                      Email Address *
                    </span>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john.doe@company.com"
                      className={`w-full px-4 py-3 rounded-xl border ${
                        errors.email
                          ? "border-red-300 bg-red-50"
                          : "border-emerald-200"
                      } focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-300 transition-colors duration-200 placeholder:text-gray-400`}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-2">
                        {errors.email}
                      </p>
                    )}
                  </label>
                </div>

                {/* Phone Number */}
                <div className="space-y-3">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <PhoneIcon className="w-4 h-4 mr-2 text-emerald-500" />
                      Phone Number *
                    </span>

                    <div className="w-full">
                      <div className="grid grid-cols-[90px_1fr] gap-3">
                        <select
                          name="phone_code"
                          value={formData.phone_code}
                          onChange={handleChange}
                          className="w-full h-[48px] px-2 rounded-xl border border-emerald-200 bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-300 transition-colors duration-200 text-sm"
                        >
                          {countryCodes.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.flag} {c.code}
                            </option>
                          ))}
                        </select>

                        <input
                          type="text"
                          name="phone"
                          value={formData.phone}
                          onChange={handlePhoneInput}
                          placeholder="98765 43210"
                          className={`w-full h-[48px] px-4 rounded-xl border ${
                            errors.phone
                              ? "border-red-300"
                              : "border-emerald-200"
                          } focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-300 transition-colors duration-200 placeholder:text-gray-400`}
                        />
                      </div>
                    </div>

                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-2">
                        {errors.phone}
                      </p>
                    )}
                  </label>
                </div>

                {/* Date of Joining */}
                <div className="space-y-3">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-2 text-emerald-500" />
                      Date of Joining *
                    </span>
                    <input
                      type="date"
                      name="date_of_joining"
                      value={formData.date_of_joining}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-xl border ${
                        errors.date_of_joining
                          ? "border-red-300"
                          : "border-emerald-200"
                      } focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-300 transition-colors duration-200`}
                    />
                    {errors.date_of_joining && (
                      <p className="text-red-500 text-sm mt-2">
                        {errors.date_of_joining}
                      </p>
                    )}
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="mt-8 flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-200 rounded-xl hover:bg-gray-900 hover:text-white transition-all duration-200 border border-transparent hover:border-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-green-600 rounded-xl hover:from-emerald-700 hover:to-green-700 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group relative overflow-hidden"
              >
                {isSubmitting && (
                  <svg
                    className="w-5 h-5 animate-spin relative z-10"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      strokeWidth="4"
                      stroke="currentColor"
                      opacity="0.25"
                    />
                    <path
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      opacity="0.75"
                    />
                  </svg>
                )}
                <span className="relative z-10">
                  {isSubmitting ? "Creating..." : "Create Employee"}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEmployeeForm;
